import { Box, Stack, Text, useMantineTheme } from '@mantine/core';
import { Bar, BarChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { scoreAnnualCountGoal } from '../../statusUtils';
import { calcYtdYoYChange, formatCountLabel, formatYoYSublabel } from '../../metricSeriesUtils';
import type { DashboardActiveGoal } from '../../../../types/dashboard';
import type { InTulsaActivityPoint, InTulsaAllTime } from '../types';
import { latest, pctChange, previous, yearOf } from '../helpers';
import { CardShell } from './CardShell';
import { MetricRow } from './MetricRow';

type AdHocCardProps = {
  series: InTulsaActivityPoint[];
  allTime: InTulsaAllTime;
  goals?: DashboardActiveGoal[];
};

export function AdHocCard({ series, allTime, goals }: AdHocCardProps) {
  const theme = useMantineTheme();
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);

  const currentYear = yearOf(cur?.quarter ?? '');
  const yearStartIdx = series.findIndex((p) => {
    const year = parseInt(p.quarter.split(' ')[1]);
    return year === currentYear;
  });
  const ytd = yearStartIdx >= 0 ? series.slice(yearStartIdx).reduce((acc, p) => acc + p.adHocRequestsCompleted, 0) : 0;
  const yoyPct = cur
    ? calcYtdYoYChange(series, cur.quarter, (p) => p.quarter, (p) => p.adHocRequestsCompleted)
    : null;

  const qoq = cur ? pctChange(cur.adHocRequestsCompleted, prev?.adHocRequestsCompleted ?? null) : null;
  const arrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');
  const qoqText = qoq === null ? '—' : arrow(qoq) ? `${arrow(qoq)}${Math.abs(qoq).toFixed(2)}% QoQ` : '0.00% QoQ';

  const allTimeTotal = allTime.adHocRequestsCompleted;

  const scored = scoreAnnualCountGoal({
    ytd,
    goals,
    field: 'adHocRequestsCompleted',
    year: currentYear,
    quarterLabel: cur?.quarter ?? '',
    direction: 'min',
  });
  const goalPerYear = scored.goal;
  const status = scored;

  const annualTotals = series.reduce<Record<number, number>>((acc, point) => {
    const year = yearOf(point.quarter);
    acc[year] = (acc[year] ?? 0) + point.adHocRequestsCompleted;
    return acc;
  }, {});

  const dataYears = Object.keys(annualTotals)
    .map((year) => Number(year))
    .filter((year) => Number.isFinite(year));
  const minYear = dataYears.length > 0 ? Math.min(...dataYears) : currentYear;
  const maxYear = dataYears.length > 0 ? Math.max(...dataYears) : currentYear;
  const years = Array.from({ length: maxYear - minYear + 1 }, (_, idx) => minYear + idx);
  const chartTotal = years.reduce((sum, year) => sum + (annualTotals[year] ?? 0), 0);
  const priorToRange = Math.max(0, allTimeTotal - chartTotal);

  const annualData = years.map((year) => ({
    year,
    yearLabel: String(year),
    total: (annualTotals[year] ?? 0) + (year === minYear ? priorToRange : 0),
  }));
  const maxAnnualTotal = Math.max(goalPerYear ?? 0, ...annualData.map((d) => d.total), 1);

  const tooltipShellStyle = {
    backgroundColor: theme.colorScheme === 'dark' ? '#000' : '#fff',
    border: `1px solid ${theme.colorScheme === 'dark' ? '#333' : '#ddd'}`,
    borderRadius: 10,
    color: theme.colorScheme === 'dark' ? '#fff' : '#000',
    padding: '10px 12px',
    maxWidth: 320,
    whiteSpace: 'normal' as const,
    wordBreak: 'break-word' as const,
  } as const;

  return (
    <CardShell
      title="Ad Hoc Requests Completed"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
    >
      <Stack spacing="sm">
        <MetricRow
          items={[
            {
              label: 'This Quarter',
              labelTooltip: 'Ad hoc analyses completed this quarter (at direction of Workforce Intermediary).',
              value: (
                <Text size="lg" weight={800}>
                  {cur?.adHocRequestsCompleted ?? '—'}
                </Text>
              ),
              sublabel: qoqText,
            },
            {
              label: 'YTD',
              labelTooltip:
                goalPerYear != null
                  ? `Calendar year to date ad hoc requests. Goal: at least ${goalPerYear} annually.`
                  : 'Calendar year to date ad hoc requests.',
              value: (
                <Text size="lg" weight={800}>
                  {ytd}
                </Text>
              ),
              sublabel: (
                <>
                  {formatYoYSublabel(yoyPct)}
                  {goalPerYear != null ? (
                    <>
                      <br />
                      {`Goal ≥${goalPerYear}/year`}
                    </>
                  ) : null}
                </>
              ),
            },
            {
              label: 'All-Time',
              labelTooltip: 'Total ad hoc requests completed across all time (includes periods not shown).',
              value: (
                <Text size="lg" weight={800}>
                  {allTimeTotal}
                </Text>
              ),
            },
          ]}
        />

        <Text size="xs" color="dimmed" weight={700} sx={{ letterSpacing: 0.2 }}>
          Annual Ad Hoc Requests Completed
        </Text>

        <Box sx={{ height: 170 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={annualData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.18} vertical={false} />
              <XAxis dataKey="yearLabel" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" minTickGap={12} />
              <YAxis orientation="right" tick={{ fontSize: 11 }} allowDecimals={false} axisLine={false} tickLine={false} width={44} tickMargin={8} domain={[0, maxAnnualTotal]} />
              {goalPerYear != null ? (
                <ReferenceLine y={goalPerYear} stroke="#fa5252" strokeDasharray="4 4" ifOverflow="extendDomain" />
              ) : null}
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || payload.length === 0) return null;
                  const v = payload[0]?.value;
                  return (
                    <div style={tooltipShellStyle as any}>
                      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 12, opacity: 0.9 }}>{label}</div>
                      <div style={{ fontWeight: 900, fontSize: 14, lineHeight: 1.2 }}>
                        {typeof v === 'number' ? formatCountLabel(v, 'Request') : '—'}
                      </div>
                    </div>
                  );
                }}
              />
              <Bar
                dataKey="total"
                fill={theme.colors.blue[6]}
                  barSize={18}
                activeBar={{ fill: theme.colors.blue[5] }}
                radius={[8, 8, 8, 8]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </CardShell>
  );
}
